import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Panel, AdminHeader, Stat, Field, SelectField } from "@/pages/admin/_shared";
import Visualizer from "@/components/Visualizer";
import { Sparkle, Brain, Pulse, ChartLineUp, Plus, ArrowsClockwise } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

export default function AdminAiEngine() {
  const [trending, setTrending] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ prompt: "", style_keyword: "rage", bias_drive: 0.7, bias_reverb: 0.4, bias_filter: 0.6 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, j, s] = await Promise.all([
      api.get("/admin/ai/trending"),
      api.get("/admin/ai/training-jobs"),
      api.get("/admin/ai/engine-stats"),
    ]);
    setTrending(t.data); setJobs(j.data); setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const train = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/ai/training-jobs", form);
      setForm({ ...form, prompt: "" });
      load();
    } finally { setBusy(false); }
  };

  if (!stats) return <div className="p-10 text-ink-dim">LOADING AI ENGINE…</div>;

  const chartData = trending.slice(0, 10).map((t) => ({ keyword: t.keyword.toUpperCase(), uses: t.uses, color: t.color }));

  return (
    <div className="p-6 lg:p-8" data-testid="admin-ai-engine">
      <AdminHeader section="AI SOUND ENGINE" title={["TRAIN THE ", "*MACHINE."]} right={
        <button onClick={load} data-testid="ai-refresh" className="border border-zinc-700 px-4 py-2.5 text-[10px] tracking-[0.3em] uppercase hover:border-brand-red hover:text-brand-red inline-flex items-center gap-2">
          <ArrowsClockwise size={12} weight="bold" /> REFRESH
        </button>
      } />

      {/* Engine status */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <Stat label="MODEL VERSION" value={stats.model_version} Icon={Brain} testid="ai-stat-version" />
        <Stat label="GENERATIONS" value={stats.total_generations.toLocaleString()} Icon={Sparkle} testid="ai-stat-gens" />
        <Stat label="TRAINING JOBS" value={stats.training_jobs} Icon={Pulse} testid="ai-stat-jobs" />
        <Stat label="AVG LATENCY" value={`${stats.avg_latency_ms}ms`} testid="ai-stat-latency" />
        <Stat label="STATUS" value={stats.engine_status.toUpperCase()} color="text-brand-green" testid="ai-stat-status" />
      </div>

      {/* Live waveform spectrum */}
      <Panel title="LIVE FREQUENCY SPECTRUM · ENGINE OUTPUT" tag="LIVE" className="mb-8" testid="ai-spectrum">
        <Visualizer height={180} style="bars" />
      </Panel>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Training form */}
        <Panel title="TRAIN NEW PRESET CLASS" tag="MOD A" testid="ai-train-form">
          <form onSubmit={train} className="space-y-3">
            <Field label="Training prompt" value={form.prompt} onChange={(v) => setForm({ ...form, prompt: v })} placeholder="reverb-heavy late-night carti lead…" testid="ai-train-prompt" multiline required />
            <SelectField label="Style anchor" value={form.style_keyword} onChange={(v) => setForm({ ...form, style_keyword: v })} options={stats.supported_keywords} testid="ai-train-style" />
            <div className="grid grid-cols-3 gap-2">
              <Field label="Drive bias" type="number" step="0.05" value={form.bias_drive} onChange={(v) => setForm({ ...form, bias_drive: parseFloat(v) })} testid="ai-train-drive" />
              <Field label="Reverb bias" type="number" step="0.05" value={form.bias_reverb} onChange={(v) => setForm({ ...form, bias_reverb: parseFloat(v) })} testid="ai-train-reverb" />
              <Field label="Filter bias" type="number" step="0.05" value={form.bias_filter} onChange={(v) => setForm({ ...form, bias_filter: parseFloat(v) })} testid="ai-train-filter" />
            </div>
            <button type="submit" disabled={busy || !form.prompt} data-testid="ai-train-submit" className="w-full bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold py-3 hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <Plus size={12} weight="bold" /> {busy ? "TRAINING…" : "QUEUE TRAINING JOB"}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-zinc-900">
            <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim mb-3">RECENT JOBS</div>
            <ul className="space-y-2 max-h-[200px] overflow-y-auto">
              {jobs.length === 0 ? (
                <li className="text-xs text-ink-dim">No jobs yet.</li>
              ) : jobs.map((j) => (
                <li key={j.id} className="border border-zinc-900 p-2.5 bg-black/40" data-testid={`ai-job-${j.id}`}>
                  <div className="flex items-center justify-between text-[10px] tracking-[0.25em] uppercase">
                    <span className="text-white truncate">{j.style_keyword}</span>
                    <span className={j.status === "completed" ? "text-brand-green" : "text-brand-purple animate-flicker"}>
                      ● {j.status}
                    </span>
                  </div>
                  <div className="text-[9px] text-ink-dim mt-1 truncate">{j.prompt}</div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-brand-red mt-1">{j.samples_used} SAMPLES</div>
                </li>
              ))}
            </ul>
          </div>
        </Panel>

        {/* Trending sounds */}
        <Panel title="TRENDING USER PROMPTS" tag="24H" className="lg:col-span-2" testid="ai-trending">
          {chartData.length === 0 ? (
            <div className="text-xs text-ink-dim py-10 text-center">No trending data yet — users haven&apos;t generated enough presets.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ left: -10, right: 12, top: 12, bottom: 20 }}>
                  <CartesianGrid stroke="#1a1a1a" vertical={false} />
                  <XAxis dataKey="keyword" stroke="#52525b" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} angle={-30} textAnchor="end" />
                  <YAxis stroke="#52525b" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 11 }} />
                  <Bar dataKey="uses">
                    {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {trending.slice(0, 12).map((t, i) => (
                  <span key={i} className="text-[10px] tracking-[0.2em] uppercase border px-2 py-1" style={{ borderColor: t.color, color: t.color }}>
                    #{t.keyword} <span className="text-white">·{t.uses}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}
